import { getErrorCode, getFieldErrors, type RouterOutputs } from "@onadssuper/api-client";
import {
  CAMPAIGN_CHANNEL_LABELS,
  CAMPAIGN_STATUS_LABELS,
  CAMPAIGN_STATUS_TRANSITIONS,
  type CampaignStatus,
  type CreateCampaignInput,
} from "@onadssuper/validators";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Pencil, SearchX } from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  PageHeader,
  PageSpinner,
} from "../../../../components/ui";
import { ActivityFeed } from "../../../../features/activity/activity-feed";
import { BriefSection } from "../../../../features/briefs/brief-section";
import { CampaignForm, campaignToForm } from "../../../../features/campaigns/campaign-form";
import { formatBudget } from "../../../../features/campaigns/money";
import { NextActionText } from "../../../../features/campaigns/next-action";
import { CampaignStatusBadge } from "../../../../features/campaigns/status-badge";
import { TeamDetail } from "../../../../features/campaigns/team";
import { Conversation } from "../../../../features/comments/conversation";
import { IdeasSection } from "../../../../features/ideas/ideas-section";
import { TaskBoard } from "../../../../features/tasks/task-board";
import { useCan, useCurrentWorkspace } from "../../../../features/workspace/workspace-context";
import { Avatar } from "../../../../lib/avatar";
import { useTRPC } from "../../../../lib/trpc";
import { formatCalendarDate, formatDateTime } from "../../../../lib/utils";

export const Route = createFileRoute("/w/$slug/campaigns/$campaignId")({ component: CampaignPage });

type Campaign = RouterOutputs["campaigns"]["get"];

/** Status changes offered as buttons, worded as actions. */
const ACTION_LABELS: Record<CampaignStatus, string> = {
  planning: "Restore",
  active: "Mark active",
  paused: "Pause",
  completed: "Mark completed",
  archived: "Archive",
};

function CampaignPage() {
  const { campaignId } = Route.useParams();
  const workspace = useCurrentWorkspace();
  const trpc = useTRPC();
  const campaign = useQuery(trpc.campaigns.get.queryOptions({ campaignId }));
  const [editing, setEditing] = useState(false);

  if (campaign.isPending) return <PageSpinner />;
  if (campaign.isError) {
    if (
      getErrorCode(campaign.error) === "NOT_FOUND" ||
      getErrorCode(campaign.error) === "BAD_REQUEST"
    )
      return (
        <EmptyState
          icon={<SearchX className="size-8" />}
          title="Campaign not found"
          description="It may have been removed, or the link is wrong."
          action={
            <Button asChild variant="secondary">
              <Link to="/w/$slug/campaigns" params={{ slug: workspace.slug }}>
                Back to campaigns
              </Link>
            </Button>
          }
        />
      );
    return <ErrorState error={campaign.error} onRetry={() => void campaign.refetch()} />;
  }

  return (
    <>
      <Link
        to="/w/$slug/campaigns"
        params={{ slug: workspace.slug }}
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-text"
      >
        <ArrowLeft className="size-4" aria-hidden /> Campaigns
      </Link>
      {editing ? (
        <EditCampaign campaign={campaign.data} onDone={() => setEditing(false)} />
      ) : (
        <Overview campaign={campaign.data} onEdit={() => setEditing(true)} />
      )}
    </>
  );
}

function Overview({ campaign, onEdit }: { campaign: Campaign; onEdit: () => void }) {
  const canUpdate = useCan("campaign.update");
  const archived = campaign.status === "archived";
  return (
    <>
      <PageHeader
        title={campaign.name}
        description={<CampaignStatusBadge status={campaign.status} />}
        actions={
          canUpdate && (
            <>
              <StatusActions campaign={campaign} />
              {!archived && (
                <Button variant="secondary" onClick={onEdit}>
                  <Pencil className="size-4" /> Edit
                </Button>
              )}
            </>
          )
        }
      />
      {campaign.nextAction && (
        <div className="mb-4 rounded-lg border border-border bg-surface-muted px-4 py-3 text-sm">
          <NextActionText action={campaign.nextAction} />
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="flex flex-col gap-4 lg:col-span-2">
          <TextCard title="Objective" text={campaign.objective} empty="No objective written yet." />
          <TextCard title="Audience" text={campaign.audience} empty="No audience described yet." />
        </div>
        <Card>
          <CardHeader title="Details" />
          <CardBody>
            <dl className="flex flex-col gap-4 text-sm">
              <Detail label="Owner">
                <span className="flex items-center gap-2">
                  <Avatar name={campaign.owner.name} image={campaign.owner.image} />
                  {campaign.owner.name}
                </span>
              </Detail>
              <Detail label="Team">
                <TeamDetail campaign={campaign} />
              </Detail>
              <Detail label="Dates">
                {campaign.startsOn ? formatCalendarDate(campaign.startsOn) : "Not set"} –{" "}
                {campaign.endsOn ? formatCalendarDate(campaign.endsOn) : "not set"}
              </Detail>
              <Detail label="Budget envelope">
                {formatBudget(campaign.budgetAmountMinor, campaign.budgetCurrency)}
              </Detail>
              <Detail label="Channels">
                {campaign.channels.map((c) => CAMPAIGN_CHANNEL_LABELS[c]).join(", ") || "None yet"}
              </Detail>
              <Detail label="Created">{formatDateTime(campaign.createdAt)}</Detail>
              {campaign.updatedAt && (
                <Detail label="Last changed">{formatDateTime(campaign.updatedAt)}</Detail>
              )}
            </dl>
          </CardBody>
        </Card>
      </div>
      <div className="mt-4">
        <BriefSection campaignId={campaign.id} campaignArchived={archived} />
      </div>
      <div className="mt-4">
        <TaskBoard campaignId={campaign.id} campaignArchived={archived} />
      </div>
      <div className="mt-4">
        <IdeasSection campaignId={campaign.id} campaignArchived={archived} />
      </div>
      <div className="mt-4">
        <Conversation campaignId={campaign.id} campaignArchived={archived} />
      </div>
      <div className="mt-4">
        <ActivityFeed campaignId={campaign.id} />
      </div>
    </>
  );
}

const Detail = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <dt className="text-xs font-medium tracking-wide text-subtle uppercase">{label}</dt>
    <dd className="mt-1">{children}</dd>
  </div>
);

const TextCard = ({ title, text, empty }: { title: string; text: string; empty: string }) => (
  <Card>
    <CardHeader title={title} />
    <CardBody>
      {text ? (
        <p className="text-sm whitespace-pre-wrap">{text}</p>
      ) : (
        <p className="text-sm text-subtle">{empty}</p>
      )}
    </CardBody>
  </Card>
);

/** On a version conflict, reload the campaign and tell the person what happened. */
const useConflictReload = (campaignId: string) => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return async (error: { message: string }) => {
    if (getErrorCode(error) === "CONFLICT") {
      toast.error("Someone else changed this campaign. Showing the latest version.");
      await queryClient.invalidateQueries({
        queryKey: trpc.campaigns.get.queryKey({ campaignId }),
      });
      return true;
    }
    return false;
  };
};

function StatusActions({ campaign }: { campaign: Campaign }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const reloadOnConflict = useConflictReload(campaign.id);
  const setStatus = useMutation(
    trpc.campaigns.setStatus.mutationOptions({
      meta: { silent: true },
      onSuccess: async (updated) => {
        toast.success(`Campaign is now ${CAMPAIGN_STATUS_LABELS[updated.status].toLowerCase()}`);
        await queryClient.invalidateQueries({ queryKey: trpc.campaigns.pathKey() });
      },
      onError: async (error) => {
        if (!(await reloadOnConflict(error))) toast.error(error.message);
      },
    }),
  );
  return CAMPAIGN_STATUS_TRANSITIONS[campaign.status].map((next) => (
    <Button
      key={next}
      variant={next === "archived" ? "ghost" : "secondary"}
      loading={setStatus.isPending && setStatus.variables?.status === next}
      disabled={setStatus.isPending}
      onClick={() =>
        setStatus.mutate({
          campaignId: campaign.id,
          status: next,
          expectedVersion: campaign.version,
        })
      }
    >
      {ACTION_LABELS[next]}
    </Button>
  ));
}

/** Only the fields that differ from the stored campaign are sent. */
const EDITABLE = [
  "name",
  "objective",
  "audience",
  "startsOn",
  "endsOn",
  "budgetAmountMinor",
  "budgetCurrency",
  "channels",
  "ownerUserId",
] as const satisfies readonly (keyof CreateCampaignInput)[];

const changedFields = (campaign: Campaign, input: CreateCampaignInput) => {
  const changes: Partial<CreateCampaignInput> = {};
  for (const key of EDITABLE) {
    if (JSON.stringify(input[key]) !== JSON.stringify(campaign[key]))
      Object.assign(changes, { [key]: input[key] });
  }
  return changes;
};

function EditCampaign({ campaign, onDone }: { campaign: Campaign; onDone: () => void }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const reloadOnConflict = useConflictReload(campaign.id);
  const canAssign = useCan("campaign.assign");
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();
  const save = useMutation(
    trpc.campaigns.update.mutationOptions({
      meta: { silent: true },
      onSuccess: async () => {
        toast.success("Campaign saved");
        await queryClient.invalidateQueries({ queryKey: trpc.campaigns.pathKey() });
        onDone();
      },
      onError: async (error) => {
        if (await reloadOnConflict(error)) return onDone();
        const fields = getFieldErrors(error);
        setServerErrors(fields);
        setFormError(Object.keys(fields).length ? undefined : error.message);
      },
    }),
  );
  return (
    <>
      <PageHeader title={`Edit ${campaign.name}`} />
      <Card className="max-w-3xl">
        <CardBody>
          <CampaignForm
            initial={campaignToForm(campaign)}
            submitLabel="Save changes"
            submitting={save.isPending}
            serverErrors={serverErrors}
            formError={formError}
            lockOwner={!canAssign}
            onCancel={onDone}
            onSubmit={(input) => {
              const changes = changedFields(campaign, input);
              if (Object.keys(changes).length === 0) return onDone();
              save.mutate({
                campaignId: campaign.id,
                expectedVersion: campaign.version,
                ...changes,
              });
            }}
          />
        </CardBody>
      </Card>
    </>
  );
}
